import {useEffect, useState} from 'react';

import useComponentIsMounted from '@netx/core-components/hooks/useComponentIsMounted';
import {useWhenChangedEffect} from '@netx/core-components/hooks/useWhenChangedEffect';
import {useSavedSearches} from './useSavedSearches';
import type {SavedSearch, SavedSearchExtended} from '../types';
import {getSearch} from '../api';

export type UseSavedSearchArguments<Extended extends boolean = false> = {
	id?: number;
	extended?: Extended;
};

export const useSavedSearch = <Extended extends boolean = false>({
	id,
	extended = false as Extended,
}: UseSavedSearchArguments<Extended>) => {
	const [savedSearch, setSavedSearch] =
		useState<[Extended] extends [false] ? SavedSearch : SavedSearchExtended>();
	const [loading, setLoading] = useState(false);

	const {savedSearches} = useSavedSearches();

	const {act} = useComponentIsMounted();

	useWhenChangedEffect(
		[id, savedSearches] as const,
		([id, savedSearches]) => {
			const savedSearchData =
				id && savedSearches.find((savedSearch) => savedSearch.id === id);

			if (!savedSearchData) {
				setSavedSearch(undefined);
				return;
			}

			if (extended === false) {
				setSavedSearch(savedSearchData as any);
				return;
			}

			setLoading(true);

			getSearch({id})
				.then((bean) => {
					act(() =>
						setSavedSearch({
							...savedSearchData,
							...bean,
						}),
					);
				})
				.finally(() => {
					act(() => setLoading(false));
				});
		},
		{
			executeAtMount: true,
		},
	);

	return {
		savedSearch,
		loading,
	};
};

export default useSavedSearch;
